import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

/**
 * Identity returned by GET /auth/me. The frontend asks the backend
 * "who am I?" rather than decoding the JWT itself — keeps signature
 * validation server-side.
 */
export interface CurrentUser {
    sub: string;
    identifier: string;
    first_name: string;
    last_name: string;
}

const TOKEN_STORAGE_KEY = 'pepper_access_token';
const RETURN_PATH_STORAGE_KEY = 'pepper_sso_return_path';

// Wall-clock ms of the user's last interaction. Lives in localStorage
// (not sessionStorage) so the idle-logout clock is shared across every
// tab of the session and survives a page reload. Written here on each
// fresh token — login (re)starts the clock — and cleared on logout; the
// useIdleLogout composable updates it on activity and reads it to decide
// when to warn and when to sign the user out. See [[useIdleLogout]].
export const LAST_ACTIVITY_KEY = 'pepper_last_activity';

// Sentinel emitted by the backend when OIDC_DEV_BYPASS=true. Must stay
// in sync with auth_router.py:DEV_BYPASS_TOKEN. The store exposes
// `isDevBypass` so UI code (e.g. UserMenu) can branch on it without
// reaching for the magic string itself.
const DEV_BYPASS_TOKEN_SENTINEL = 'DEV-BYPASS-NOT-A-REAL-JWT';

/**
 * Whitelist for the deep-link round-trip: only same-origin paths are
 * accepted, never absolute URLs. This blocks an open-redirect even if
 * something planted a hostile value in sessionStorage.
 *
 * Accept: starts with "/", not "//" (protocol-relative), no backslashes,
 * ≤ 2048 chars. Reject everything else.
 */
function isSafeReturnPath(value: unknown): value is string {
    if (typeof value !== 'string') return false;
    if (value.length === 0 || value.length > 2048) return false;
    if (!value.startsWith('/')) return false;
    if (value.startsWith('//')) return false;
    if (value.includes('\\')) return false;
    return true;
}


/**
 * Auth store. Token lives in sessionStorage (per-tab; gone when the
 * tab closes) so an XSS-in-another-tab can't pivot to ours and a page
 * refresh doesn't bounce the user through the IdP.
 *
 * Flow:
 * - login(returnTo?, opts?): stash the validated return path (if
 *   provided), then full-page navigation to /auth/login. Backend does
 *   the OAuth dance and redirects to /login-complete#token=... — the
 *   AuthCallback component reads the fragment, consumes the stashed
 *   path, and navigates back. opts.selectAccount adds
 *   ?prompt=select_account so the IdP shows its account picker; the
 *   splash sets it for explicit sign-in, the 401 interceptor omits it
 *   so an expired token renews silently.
 * - loadCurrentUser(): GET /auth/me with the Bearer token. On 401 we
 *   clear the token; the router guard will then bounce to login.
 * - logout(): POST /auth/logout (best-effort audit write) then go
 *   local — clear the token + any stashed return path and land on the
 *   /login splash. We deliberately do NOT end the IdP session (mirrors
 *   lineal-tenant-ui), so the user stays signed in to Microsoft and
 *   re-login shows the account picker.
 *
 * Deep-link restoration uses sessionStorage (per-tab) rather than
 * localStorage so two tabs deep-linking simultaneously don't collide.
 */
export const useAuthStore = defineStore('auth', () => {
    const token = ref<string | null>(
        typeof sessionStorage !== 'undefined'
            ? sessionStorage.getItem(TOKEN_STORAGE_KEY)
            : null,
    );
    const user = ref<CurrentUser | null>(null);

    const isAuthenticated = computed(() => token.value !== null);

    // True when we're holding the backend's dev-bypass sentinel. Used by
    // useIdleLogout to skip the inactivity timer for dev sessions (there's
    // no real session to end). Sign-out still works under bypass — it
    // clears the token and lands on the /login splash without looping.
    const isDevBypass = computed(
        () => token.value === DEV_BYPASS_TOKEN_SENTINEL,
    );

    function setToken(newToken: string): void {
        token.value = newToken;
        sessionStorage.setItem(TOKEN_STORAGE_KEY, newToken);
        // A fresh token means a fresh session, so (re)start the idle clock.
        // A page reload rehydrates the token from sessionStorage without
        // calling setToken, so reloading can't be used to extend an idle
        // session — only an actual new sign-in resets the clock here.
        try {
            localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
        } catch {
            // localStorage unavailable (private mode / SSR / tests) — idle
            // logout is a best-effort enhancement, not a load-bearing path.
        }
    }

    function clearToken(): void {
        token.value = null;
        user.value = null;
        sessionStorage.removeItem(TOKEN_STORAGE_KEY);
        try {
            localStorage.removeItem(LAST_ACTIVITY_KEY);
        } catch {
            // See setToken — best-effort.
        }
    }

    async function loadCurrentUser(): Promise<CurrentUser | null> {
        if (!token.value) {
            user.value = null;
            return null;
        }
        try {
            const response = await fetch('/auth/me', {
                headers: { Authorization: `Bearer ${token.value}` },
            });
            if (response.status === 401) {
                clearToken();
                return null;
            }
            if (!response.ok) {
                throw new Error(
                    `GET /auth/me failed: ${response.status} ${response.statusText}`,
                );
            }
            user.value = (await response.json()) as CurrentUser;
            return user.value;
        } catch (err) {
            clearToken();
            throw err;
        }
    }

    function login(
        returnTo?: string,
        opts?: { selectAccount?: boolean },
    ): void {
        // Stash the return path so AuthCallback can restore the deep
        // link after the OIDC round-trip. Callers pass the path the user
        // is on / heading to; the helper drops it if it doesn't pass the
        // safe-path check (which also handles `undefined`).
        if (returnTo !== undefined && isSafeReturnPath(returnTo)) {
            sessionStorage.setItem(RETURN_PATH_STORAGE_KEY, returnTo);
        } else {
            // No path or unsafe path → make sure we don't restore a
            // stale value from a previous login attempt.
            sessionStorage.removeItem(RETURN_PATH_STORAGE_KEY);
        }
        // Full-page navigation: backend handles the OIDC dance and
        // redirects to /login-complete#token=... on success.
        // prompt=select_account surfaces the IdP's account picker for an
        // explicit sign-in (since we keep the IdP session alive across
        // logout, this is what lets the user choose / switch accounts
        // instead of being silently single-signed-on).
        const target = opts?.selectAccount
            ? '/auth/login?prompt=select_account'
            : '/auth/login';
        window.location.assign(target);
    }

    async function logout(): Promise<void> {
        // Best-effort server-side audit hook, fired before we go local.
        // POST /auth/logout records a LOGOUT row for this token's identity
        // (covers both explicit sign-out and the idle-logout timer). We
        // await it so the row lands before navigation, but wrap it so a
        // failure — network error, or an already-expired token the backend
        // 401s — never blocks the local sign-out. Dev-bypass sessions skip
        // the call: there's no real session to audit and the backend
        // ignores them anyway.
        const currentToken = token.value;
        if (currentToken && !isDevBypass.value) {
            try {
                await fetch('/auth/logout', {
                    method: 'POST',
                    headers: { Authorization: `Bearer ${currentToken}` },
                });
            } catch {
                // Local sign-out proceeds regardless — see above.
            }
        }
        // Local-only sign-out. Clear our token + any stashed return path,
        // then land on the /login splash. We intentionally do NOT contact
        // the IdP's end-session endpoint: that would sign the user out of
        // Microsoft entirely (forcing a full credential re-entry) and,
        // called without an id_token_hint, behaved flakily (a first click
        // that left the session intact). Keeping the IdP session means the
        // next sign-in just shows the account picker. Mirrors
        // lineal-tenant-ui's logout.
        clearToken();
        sessionStorage.removeItem(RETURN_PATH_STORAGE_KEY);
        window.location.assign('/login');
    }

    /**
     * Read-and-clear the stashed return path. Returns null when nothing
     * was stashed, when the stashed value fails the safe-path check, or
     * when sessionStorage isn't available. AuthCallback calls this once
     * after setToken() to decide where to land.
     */
    function consumeStashedReturnPath(): string | null {
        const stashed = sessionStorage.getItem(RETURN_PATH_STORAGE_KEY);
        sessionStorage.removeItem(RETURN_PATH_STORAGE_KEY);
        return isSafeReturnPath(stashed) ? stashed : null;
    }

    return {
        token,
        user,
        isAuthenticated,
        isDevBypass,
        setToken,
        clearToken,
        loadCurrentUser,
        login,
        logout,
        consumeStashedReturnPath,
    };
});
